import { useMemo } from 'react';

import { OnboardingStep, OnboardingSteps } from '@portal-clients/user';

import { usePushNotifications } from '@frontend/notifications';
import { useUser } from '@frontend/utils';

export interface UseOnboardingValue {
  shouldOnboard: boolean;
  stepsPerformed: OnboardingStep[];
  stepsRemaining: OnboardingStep[];
}

export const useOnboarding = () => {
  const { user } = useUser();
  const pushNotifications = usePushNotifications();

  const validOnboardingSteps = useMemo((): OnboardingStep[] => {
    return OnboardingSteps.filter((step) => {
      if (step === 'PUSH_NOTIFICATIONS') {
        return (
          pushNotifications.isSupported &&
          pushNotifications.canRequestPermission
        );
      }

      return true;
    });
  }, [pushNotifications.isSupported, pushNotifications.canRequestPermission]);

  const value = useMemo((): UseOnboardingValue => {
    if (!user) {
      return {
        shouldOnboard: false,
        stepsPerformed: [],
        stepsRemaining: [],
      };
    }

    const onboarding = user.preferences?.onboarding;

    if (onboarding) {
      // We have already done some onboarding. We need to find out which version of the
      // onboarding mechanic they have done and if they need to do more steps.
      if (onboarding.stepsPerformed !== undefined) {
        const stepsRemaining = validOnboardingSteps.filter(
          (step) => !onboarding.stepsPerformed!.includes(step),
        );

        return {
          shouldOnboard: stepsRemaining.length > 0,
          stepsPerformed: onboarding.stepsPerformed,
          stepsRemaining,
        };
      }

      // Old onboarding preference without stepsPerformed relied on a version
      // number. We are unable to use that since due to licenses and features
      // can change the steps needed to onboard is very dynamic. Here we transform
      // the old preference to the new one by converting version 1 to having done
      // the NAME_AND_LANGUAGE step.
      const migratedSteps: OnboardingStep[] =
        onboarding.version === 1 ? ['NAME_AND_LANGUAGE'] : [];

      const stepsRemaining = validOnboardingSteps.filter(
        (step) => !migratedSteps!.includes(step),
      );

      return {
        shouldOnboard: stepsRemaining.length > 0,
        stepsPerformed: migratedSteps,
        stepsRemaining,
      };
    }

    // New user without any onboarding done
    return {
      shouldOnboard: true,
      stepsPerformed: [],
      stepsRemaining: validOnboardingSteps,
    };
  }, [user, validOnboardingSteps]);

  return value;
};
